
import {styled} from '@mui/system'
import {Calendar} from 'react-big-calendar'
import {alpha} from '@mui/material/styles'
import {APP_BACKGROUND, CARD_BORDER_RADIUS, NEUTRAL_SCALE} from 'src/theme-new/tokens'

export const CalendarStyledNew = styled(Calendar)(({theme}) => ({

  // ---------------------------------------------------------------------------
  // Month / time-grid containers
  // ---------------------------------------------------------------------------

  '.rbc-month-view': {
    background: NEUTRAL_SCALE[50],
    border: `1px solid ${NEUTRAL_SCALE[200]}`,
    borderRadius: CARD_BORDER_RADIUS,
    overflow: 'hidden',
  },

  '.rbc-time-view': {
    background: NEUTRAL_SCALE[50],
    border: `1px solid ${NEUTRAL_SCALE[200]}`,
    borderRadius: CARD_BORDER_RADIUS,
    overflow: 'hidden',
  },

  '.rbc-month-row': {
    overflow: 'visible',
  },

  // ---------------------------------------------------------------------------
  // Grid lines — use token instead of react-big-calendar's default gray
  // ---------------------------------------------------------------------------

  '.rbc-day-bg + .rbc-day-bg': {
    borderLeft: `1px solid ${NEUTRAL_SCALE[200]}`,
  },

  '.rbc-month-row + .rbc-month-row': {
    borderTop: `1px solid ${NEUTRAL_SCALE[200]}`,
  },

  '.rbc-time-content': {
    borderTop: `1px solid ${NEUTRAL_SCALE[200]}`,
  },

  '.rbc-timeslot-group': {
    borderBottom: `1px solid ${NEUTRAL_SCALE[200]}`,
  },

  // ---------------------------------------------------------------------------
  // Day header row (lun / mar / mer …)
  // ---------------------------------------------------------------------------

  '.rbc-header': {
    backgroundColor: NEUTRAL_SCALE[50],
    color: NEUTRAL_SCALE[500],
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'capitalize',
    padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
    borderBottom: `1px solid ${NEUTRAL_SCALE[200]}`,

    '& a': {
      color: 'inherit',
      textDecoration: 'none',
    },

    '&.rbc-today': {
      backgroundColor: `${alpha(theme.palette.primary.light, 0.4)} !important`,
      color: theme.palette.primary.main,
    },
  },

  // ---------------------------------------------------------------------------
  // Date number in month cells
  // ---------------------------------------------------------------------------

  '.rbc-date-cell': {
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
    fontSize: '13px',
    color: NEUTRAL_SCALE[700],
    fontWeight: 500,
    textAlign: 'right',

    '& a': {
      color: 'inherit',
      textDecoration: 'none',
    },

    '&.rbc-now a': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      fontWeight: 700,
    },
  },

  // Wrapper for the holiday-name label + day number (custom month dateHeader)
  '.rbc-date-cell-inner': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },

  // Small Italian holiday name label to the left of the day number (e.g. "Natale").
  // Wraps instead of truncating — long names ("Festa della Liberazione") would
  // always get cut off with a single-line ellipsis at this cell width.
  '.holiday-label': {
    fontSize: '9px',
    fontWeight: 600,
    lineHeight: 1.2,
    color: NEUTRAL_SCALE[500],
    textTransform: 'none',
    maxWidth: '56px',
    wordBreak: 'break-word',
  },

  // ---------------------------------------------------------------------------
  // Today highlight
  // ---------------------------------------------------------------------------

  '.rbc-today': {
    backgroundColor: alpha(theme.palette.primary.light, 0.25),
  },

  // ---------------------------------------------------------------------------
  // Off-range days (outside current month)
  // ---------------------------------------------------------------------------

  '.rbc-off-range-bg': {
    backgroundColor: NEUTRAL_SCALE[100],
  },

  '.rbc-off-range': {
    color: NEUTRAL_SCALE[300],
  },

  // ---------------------------------------------------------------------------
  // Sunday / holiday tint (applied via dayPropGetter class — rbc-holiday-day).
  // Swaps the two existing weekday/weekend tones (was: weekday=APP_BACKGROUND,
  // weekend=NEUTRAL_SCALE[50] via .rbc-month-view/.rbc-time-view above) rather
  // than introducing a new color. Excluded from .rbc-today so today always
  // takes priority. Targets both the month-view cell (.rbc-day-bg) and the
  // whole day column in Week/Day view (.rbc-day-slot) — react-big-calendar
  // applies dayPropGetter's className to different elements per view, and
  // coloring the day-slot column colors every time slot within it.
  // ---------------------------------------------------------------------------

  '.rbc-day-bg.rbc-holiday-day:not(.rbc-today), .rbc-day-slot.rbc-holiday-day:not(.rbc-today)': {
    backgroundColor: APP_BACKGROUND,
  },

  // ---------------------------------------------------------------------------
  // Day cell hover — excludes today to avoid clobbering primary.light
  // ---------------------------------------------------------------------------

  '.rbc-day-bg:not(.rbc-today):hover': {
    backgroundColor: NEUTRAL_SCALE[100],
    transition: 'background-color 0.15s ease',
    cursor: 'pointer',
  },

  // ---------------------------------------------------------------------------
  // Time-view labels
  // ---------------------------------------------------------------------------

  '.rbc-label': {
    fontSize: '13px',
    color: NEUTRAL_SCALE[500],
  },

  '.rbc-slot-selection': {
    backgroundColor: alpha(theme.palette.primary.light, 0.35),
  },

  // ---------------------------------------------------------------------------
  // Events — reduced borderRadius (12px chip is too large for small cells)
  // ---------------------------------------------------------------------------

  '.rbc-event': {
    backgroundColor: theme.palette.primary.light,
    lineHeight: '1.3',
    border: 'unset',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    color: NEUTRAL_SCALE[900],
    transition: 'opacity 0.12s ease',

    '&:hover': {
      opacity: 0.82,
    },

    '.rbc-event-label': {
      display: 'none',
    },

    // Anchor for the "Presa visione" corner badge rendered inside EventBox
    '.event-box': {
      position: 'relative',
    },

    '.event-reason': {
      fontSize: '12px',
      paddingLeft: theme.spacing(1.5),
    },

    '.event-chip': {
      width: theme.spacing(0.375),
      height: '100%',
      backgroundColor: theme.palette.primary.main,
      float: 'left',
      display: 'inline',
      marginRight: theme.spacing(0.5),
    },
  },

  // ---------------------------------------------------------------------------
  // Agenda / Lista view
  // ---------------------------------------------------------------------------

  '.rbc-agenda-table': {
    thead: {
      display: 'none',
    },

    '.rbc-agenda-time-cell': {
      display: 'none',
    },

    tr: {
      display: 'flex',
      flexDirection: 'column',
    },

    '.rbc-agenda-date-cell': {
      background: NEUTRAL_SCALE[100],
      padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

      '.date-box': {
        display: 'flex',
        justifyContent: 'space-between',
        textTransform: 'capitalize',
      },
    },

    '.rbc-agenda-event-cell': {
      padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
      borderLeft: `2px solid ${theme.palette.primary.main}`,
      background: APP_BACKGROUND,
      transition: 'background-color 0.12s ease',

      '&:hover': {
        backgroundColor: NEUTRAL_SCALE[50],
        cursor: 'pointer',
      },

      '.time-label': {
        paddingRight: theme.spacing(1),
      },

      '.event-chip': {
        display: 'inline-block',
        width: theme.spacing(0.375),
        height: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        marginLeft: theme.spacing(1),
      },

      '.title-label': {
        paddingLeft: theme.spacing(1),
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Mobile adjustments (< md / 900px)
  // ---------------------------------------------------------------------------

  [theme.breakpoints.down('md')]: {
    '.rbc-header': {
      fontSize: '11px',
      padding: `${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
    },

    '.rbc-date-cell': {
      fontSize: '11px',
      padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
    },

    // Hidden on mobile — day columns are too narrow to fit a readable label
    '.holiday-label': {
      display: 'none',
    },

    '.rbc-event': {
      borderRadius: '4px',

      '.event-reason': {
        fontSize: '11px',
      },
    },

    '.rbc-label': {
      fontSize: '11px',
    },

    '.rbc-agenda-table': {
      '.rbc-agenda-date-cell': {
        padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
      },
      '.rbc-agenda-event-cell': {
        padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
      },
    },
  },
}))
